<template>
    <div class="rich-doc fs-mask" data-testid="rich-artifact-doc">
        <template v-for="(block, index) in blocks" :key="index">
            <!-- Section rhythm: more air above a heading than below it, so the eye finds sections before it reads them. -->
            <component :is="headingTags[index]" v-if="block.type === 'heading'" :class="block.level === 3 ? 'title-body mt-4' : 'title-section mt-6'" class="text-ink first:mt-0">{{
                block.text
            }}</component>

            <MarkdownText v-else-if="block.type === 'text'" :text="block.markdown" />

            <div v-else-if="block.type === 'callout'" class="callout" :class="`callout-${block.tone ?? 'info'}`">
                <i class="callout-icon bi" :class="calloutIcon(block.tone)" aria-hidden="true"></i>
                <div class="min-w-0">
                    <p v-if="block.title" class="scaffold-body-small-medium">{{ block.title }}</p>
                    <p class="scaffold-body-small">{{ block.body }}</p>
                </div>
            </div>

            <dl v-else-if="block.type === 'stats'" class="stats">
                <div v-for="item in block.items" :key="item.label" class="stat">
                    <dt class="stat-label">{{ item.label }}</dt>
                    <dd class="stat-value">{{ item.value }}</dd>
                    <dd v-if="item.delta" class="stat-delta">{{ item.delta }}</dd>
                </div>
            </dl>

            <RichBarChart v-else-if="block.type === 'bar_chart'" :labels="block.labels" :values="block.values" :title="block.title" :unit="block.unit" />

            <div v-else-if="block.type === 'checklist'" class="checklist">
                <ul class="checklist-items">
                    <li v-for="(item, i) in checklists[index]" :key="i">
                        <BaseCheckbox
                            v-model="checklists[index][i].done"
                            shape="square"
                            class="body-small text-control-label items-start"
                            @update:model-value="touched[index] = true">
                            <span :class="{ 'text-ink-muted line-through': item.done }">{{ item.label }}</span>
                            <span v-if="item.note" class="details block text-ink-muted">{{ item.note }}</span>
                        </BaseCheckbox>
                    </li>
                </ul>
                <!-- Mounted empty, text only once the list has been touched or arrived with ticks: an untouched list's
                     "0 of n" is noise, but after an untick the count must still say 0, and a live region created
                     together with its message is never announced. -->
                <p class="checklist-progress empty:sr-only" role="status" aria-live="polite">
                    <template v-if="touched[index] || doneCount(index) > 0">{{
                        t("artifacts.panel.checklistProgress", { done: doneCount(index), total: checklists[index].length })
                    }}</template>
                </p>
            </div>

            <div v-else-if="block.type === 'progress'" class="progress">
                <p class="body-small text-ink">{{ block.label }}</p>
                <BaseProgressBar :value="block.value" show-label :aria-label="block.label" />
            </div>

            <!-- A wide comparison scrolls sideways; the region takes focus so keyboard users can reach the far columns. -->
            <div v-else-if="block.type === 'table'" class="table-scroll" role="region" tabindex="0" :aria-label="block.caption || t('artifacts.panel.comparisonTable')">
                <table class="doc-table">
                    <caption v-if="block.caption" class="table-caption">
                        {{
                            block.caption
                        }}
                    </caption>
                    <thead>
                        <tr>
                            <th v-for="(column, c) in block.columns" :key="c" scope="col">{{ column }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- The first cell names the row's criterion, so a screen reader can pair each value with it. -->
                        <tr v-for="(row, r) in block.rows" :key="r">
                            <th scope="row">{{ row[0] }}</th>
                            <td v-for="(cell, c) in row.slice(1)" :key="c">{{ cell }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Native markers are replaced by the disc, so the ordinal stays readable text; the explicit role keeps
                 list semantics in WebKit, which drops them from a list styled with list-style: none. -->
            <!-- eslint-disable-next-line vuejs-accessibility/no-redundant-roles -- WebKit removes list semantics when list-style is none -->
            <ol v-else-if="block.type === 'steps'" class="steps" role="list">
                <li v-for="(step, i) in block.items" :key="i" class="step">
                    <span class="step-number">{{ i + 1 }}</span>
                    <div class="step-body">
                        <p class="body-small-semibold text-ink">{{ step.title }}</p>
                        <p v-if="step.detail" class="body-small text-ink-muted">{{ step.detail }}</p>
                    </div>
                </li>
            </ol>

            <HorizontalDivider v-else-if="block.type === 'divider'" />

            <!-- Unknown/newer block types degrade to readable text instead of breaking the document. -->
            <p v-else class="details text-ink-muted">{{ t("artifacts.panel.unsupportedBlock", { type: unsupportedType(block) }) }}</p>
        </template>
    </div>
</template>

<script setup lang="ts">
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import RichBarChart from "./RichBarChart.vue";
import type { RichBlock, RichTone, RichUiSpec } from "./types";

// `title` is the artifact's card title, which the pane shows above the document.
const props = defineProps<{ spec: RichUiSpec; title?: string }>();
const { t } = useI18n();

const normalise = (text: string) =>
    text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();

// A document that opens by repeating its own title would show it twice; the prompt asks the model
// not to, and stored documents from before that rule heal here.
const visibleBlocks = (spec: RichUiSpec, title: string | undefined): RichBlock[] => {
    const [first, ...rest] = spec.blocks;
    return title && first?.type === "heading" && normalise(first.text) === normalise(title) ? rest : spec.blocks;
};
const blocks = computed(() => visibleBlocks(props.spec, props.title));

// The outline sits under the pane's h2, so a section is h3 and its subsections h4. A subsection
// with no section above it (the dropped duplicate was the section) is h3, so no level is skipped;
// it keeps its subsection styling.
const headingTags = computed(() => {
    let inSection = false;
    return blocks.value.map((block) => {
        if (block.type !== "heading") {
            return undefined;
        }
        if (block.level !== 3) {
            inSection = true;
            return "h3";
        }
        return inSection ? "h4" : "h3";
    });
});

// Checklist toggles are local view state — ticking a box is the user's, not a new artifact version.
// The panel keeps this component mounted when a new version's spec arrives, so rebuild on change
// rather than trusting mounts: revised labels/rows must reflect the new tree.
const buildChecklists = (visible: RichBlock[]) => visible.map((block) => (block.type === "checklist" ? block.items.map((item) => ({ ...item, done: item.done ?? false })) : []));
const checklists = ref(buildChecklists(blocks.value));
const touched = ref<boolean[]>([]);
const doneCount = (index: number) => checklists.value[index]?.filter((item) => item.done).length ?? 0;
watch(blocks, (visible) => {
    checklists.value = buildChecklists(visible);
    touched.value = [];
});

const CALLOUT_ICONS: Record<RichTone, string> = {
    info: "bi-info-circle-fill",
    success: "bi-check-circle-fill",
    warning: "bi-exclamation-circle-fill",
    danger: "bi-x-circle-fill",
};
const calloutIcon = (tone: RichTone | undefined) => CALLOUT_ICONS[tone ?? "info"];

// block narrows to never once the v-else catches every known type; keep the label resilient anyway.
const unsupportedType = (block: unknown): string => (typeof block === "object" && block !== null && "type" in block ? String(block.type) : "?");
</script>

<style scoped>
@reference "../../../../../css/tailwind.css";

@layer components {
    .rich-doc {
        @apply flex flex-col gap-4;
    }

    /* One boxed element per document: the callout, dressed as the design system's toast (BaseToast)
       and painted with its tokens, minus its floating shadow. Everything else sits on the page, so
       the callout is the thing that stands out rather than one card among several. */
    .callout {
        @apply flex items-start gap-3 rounded-md border px-4 py-2.5;
    }
    /* A 20px line box centres the 16px icon on the first text line, where a titled callout starts. */
    .callout-icon {
        @apply shrink-0 text-base/5;
    }
    .callout-info {
        @apply border-toast-info-border bg-toast-info-bg text-toast-info-text;
    }
    .callout-success {
        @apply border-toast-success-border bg-toast-success-bg text-toast-success-text;
    }
    .callout-warning {
        @apply border-toast-warning-border bg-toast-warning-bg text-toast-warning-text;
    }
    .callout-danger {
        @apply border-toast-error-border bg-toast-error-bg text-toast-error-text;
    }

    /* Stat tiles: a hairline, the label, then the figure. No box, so the number is the loud part. */
    .stats {
        @apply grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3;
    }
    .stat {
        @apply flex min-w-0 flex-col gap-1 border-t border-border pt-3;
    }
    .stat-label {
        @apply details text-ink-muted;
    }
    .stat-value {
        @apply display-sans-serif text-ink;
    }
    .stat-delta {
        @apply details text-ink-muted;
    }

    .checklist,
    .checklist-items {
        @apply flex flex-col gap-2;
    }
    .checklist-progress {
        @apply details text-ink-muted;
    }

    .progress {
        @apply flex flex-col gap-2;
    }

    .table-scroll {
        @apply w-full overflow-x-auto rounded-md focus-visible:ring-2 focus-visible:ring-control-accent focus-visible:outline-hidden;
    }
    .doc-table {
        @apply body-small w-full border-collapse text-left text-ink;
    }
    .table-caption {
        @apply details pb-2 text-left text-ink-muted;
    }
    .doc-table th {
        @apply body-small-semibold border-b border-border py-2 pr-4 align-top;
    }
    .doc-table td {
        @apply border-b border-border py-2 pr-4 align-top;
    }
    .doc-table th:last-child,
    .doc-table td:last-child {
        @apply pr-0;
    }
    /* A row header is a label, not a column heading: same weight as the values beside it. */
    .doc-table tbody th {
        @apply font-normal;
    }

    .steps {
        @apply flex list-none flex-col gap-3 p-0;
    }
    .step {
        @apply flex gap-3;
    }
    .step-number {
        @apply details flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-muted tabular-nums;
    }
    .step-body {
        @apply flex min-w-0 flex-col gap-1;
    }
}
</style>
